/**
 * AppHeader
 *
 * Full-width top header that wraps every authenticated screen — both
 * /location/* and /company/* surfaces. Layout from
 * `shaka-deck-mockup-shell.html`:
 *
 *   ┌─ brand · getaway · scope ───── POS launcher ───── NFC · bell · user ─┐
 *
 * Left cluster (brand + getaway + scope) is intrinsically sized — no
 * flex grow — so the gap between the cluster and the right action
 * group is the real-estate the centred POS launcher can occupy.
 *
 * Scope-aware behavior:
 *  - GetawaySwitcher's `current` mirrors the active context, so the
 *    same pill renders the location name in /location/* and the
 *    company name in /company/*.
 *  - POSLauncherButton + NfcStatusIndicator only mount in the location
 *    scope (POS terminals + NFC connectors are location-tied).
 *  - NotificationsBell + UserMenu are always present.
 */

import { Link } from 'react-router-dom';

import { useActiveContext } from '@/shared/hooks/useActiveContext';
import { CommandPalette, ScopeBadge, PersonFinder } from '@/shared/components';
import { GetawaySwitcher } from '@/shared/components/GetawaySwitcher';
import { NfcStatusIndicator } from '@/shared/components/NfcStatusIndicator';
import { POSLauncherButton } from '@/modules/pos/components/POSLauncherButton';
import { SidebarTrigger } from '@/theme/components/ui/sidebar';
import { NotificationsBell } from './NotificationsBell';
import { UserMenu } from './UserMenu';

const HEADER_HEIGHT_CLASS = 'h-14 lg:h-[60px]';

export function AppHeader() {
  const { isCompanyContext } = useActiveContext();
  const current: 'location' | 'company' = isCompanyContext ? 'company' : 'location';

  return (
    <header
      className={`sticky top-0 z-30 flex w-full items-center gap-3 border-b border-sand-200 bg-gradient-to-b from-card to-sand-50 px-3 lg:px-4 ${HEADER_HEIGHT_CLASS}`}
    >
      {/* Mobile: rail trigger to the very left. Desktop hides it via
          the sidebar primitive's responsive rules. */}
      <SidebarTrigger className="md:hidden" />

      {/* Brand — exact wordmark layout from the shell mockup:
          a 30×30 gradient logo box with "S" + a stacked wordmark
          ("shaka" small uppercase / "Deck" big serif). */}
      <Link
        to="/"
        aria-label="Shaka Deck"
        className="flex items-center gap-2.5 rounded-md py-1 transition-opacity hover:opacity-80"
      >
        <img
          src="/deck.svg"
          alt=""
          aria-hidden="true"
          className="h-[30px] w-auto shrink-0"
          draggable={false}
        />
        <span className="hidden flex-col gap-px leading-none sm:flex">
          <span className="font-sans text-[9.5px] font-medium lowercase tracking-[0.08em] text-sand-600 opacity-85">
            shaka
          </span>
          <span className="font-serif text-[21px] font-semibold leading-none tracking-[-0.015em] text-sand-900">
            Deck
          </span>
        </span>
      </Link>

      {/* Vertical hairline separating brand from the scope label. */}
      <span aria-hidden="true" className="h-7 w-px bg-sand-200" />

      {/* Scope (once) + active location/company name as plain text — not a
          button. Scope switching lives in the user menu. */}
      <ScopeBadge />
      <div className="min-w-0 max-w-[220px]">
        <GetawaySwitcher current={current} expanded />
      </div>

      {/* The flex-1 spacer is the only growing slot in the row. The
          POS launcher lives inside it and renders nothing when the
          user lacks canUseWebPOS or no web-enabled terminal exists.
          In company scope POS is hidden — no terminal pinning. */}
      <div className="flex flex-1 justify-center">
        {current === 'location' ? <POSLauncherButton /> : null}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {/* Find a person by name/email — location scope only, gated on
            view-guests or view-staff. Renders nothing otherwise. */}
        <PersonFinder />
        {/* NFC works in company scope too — a wristband identifies a person
            tenant-wide (company-staff bands resolve at any scope). POS stays
            location-only above (no terminal pinning in company scope). */}
        <NfcStatusIndicator />
        <NotificationsBell />
        <UserMenu />
      </div>

      <CommandPalette />
    </header>
  );
}
